/**
 * useBusinessSnapshot — fetch orchestration for the "My Business" band (home)
 * and, later, the /my-business page. Aug 24, 2026, Ship 3.
 *
 * Phase-1 loads run in parallel on mount (Promise.allSettled — each card
 * renders as its slice lands). A sessionStorage SWR cache paints the band
 * instantly on repeat visits, then refreshes behind. Deliberately NO realtime
 * on `deals` (whole-table churn is far too noisy — the documented
 * MyCommissionsTab choice); data refreshes on mount + manual refresh().
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import { mergeRates, ratesMapFromRows } from "../../lib/commissionRates";
import type { TeamMember } from "../../lib/types";
import { groupByPerson, groupByPersonStored } from "../commissions/CommissionBoard";
import { fetchCommissionPayments, indexPayments } from "../../lib/commissionPayments";
import {
  activityMix,
  canSeeSupplements,
  cityLens,
  claimsAgg,
  claimsByCarrier,
  closedDealsByMonth,
  fetchCityEffort,
  fetchCommCounts,
  fetchMyClaimOutcomes,
  fetchMyTerminalDeals,
  fetchStageHistory,
  leadSourceLens,
  myLines,
  stageFlow,
  sumShifts,
  teamSnapshot,
  type ActivityMix,
  type AuditShiftRow,
  type CarrierClaimRow,
  type CityLensRow,
  type LeadSourceRow,
  type StageFlowRow,
  commissionOutlook,
  earningsByMonth,
  fetchCanvasserByDeal,
  fetchDrawBalance,
  fetchMyCommitmentRow,
  fetchMySpiffs,
  fetchSnapshotDeals,
  heldCommissions,
  moneyScopeIds,
  myBacklog,
  personalFunnel,
  pipelineSummary,
  projectionSet,
  coldDeals,
  mondayOf,
  todayET,
  etDay,
  type ClosedMonthGroup,
  type ColdDeal,
  type CommissionOutlook,
  type CommitmentRowLite,
  type EarningsSummary,
  type HeldCommission,
  type PersonalFunnel,
  type PipelineSummary,
  type SnapshotDeal,
  type SpiffHistoryRow,
  type TeamMemberRow,
} from "../../lib/businessSnapshot";
import type { BacklogStats } from "../../lib/financialsData";
import { commissionYearSlice, crewYearFor, type CommissionYearSlice } from "../../lib/commissionGoals";
import {
  fetchLeaderboard,
  fetchWallStats,
  fetchWeeklyGoalsMap,
  type WeeklyGoalsMap,
} from "../crew/crewQueries";

export interface CrewSnippet {
  rank: number | null;
  of: number;
  pointsWeek: number;
  /** claims + signed IAs WTD — the Perfect Week counter (target 4). */
  perfectTotal: number;
  spiffWonWeek: number; // $ from the spiffs table, this ET week
}

export interface CoreSnapshot {
  earnings: EarningsSummary;
  outlook: CommissionOutlook;
  backlog: BacklogStats;
  pipeline: PipelineSummary;
  coldCount: number;
  heldCount: number;
  /** Detail lists for the /my-business page. */
  cold: ColdDeal[];
  held: HeldCommission[];
  closedByMonth: ClosedMonthGroup[];
  spiffs: SpiffHistoryRow[];
  funnel: PersonalFunnel;
  goals: WeeklyGoalsMap[string] | undefined; // my weekly goals
  crew: CrewSnippet;
  drawBalance: number;
  commitmentRow: CommitmentRowLite | null;
  /** TL/ASM: per-member production + the viewer's override per member. */
  team: TeamMemberRow[] | null;
  teamBacklog: BacklogStats | null;
  /** Paid lines inside the crew competition year (the goal chart's actual line). */
  commissionYear: CommissionYearSlice;
}

/** Serializable slice for the SWR cache (band paints instantly on repeat). */
interface CachedBand {
  earnedThisMonth: number;
  monthSeries: number[];
  coming: number;
  bookValue: number;
  liveCount: number;
}

export interface LensData {
  geo: CityLensRow[];
  sources: LeadSourceRow[];
  stages: StageFlowRow[];
  claims: { agg: ReturnType<typeof claimsAgg>; byCarrier: CarrierClaimRow[] };
  activity: ActivityMix;
}

/** Phase-1 artifacts retained for the lazy lens pass. */
interface Phase1Artifacts {
  me: string;
  queue: SnapshotDeal[];
  live: SnapshotDeal[];
  queueGroups: ReturnType<typeof groupByPerson>;
  projGroups: ReturnType<typeof groupByPerson>;
  members: Record<string, TeamMember>;
}

const cacheKey = (id: string) => `bp_mybiz_${id}`;

export function readCachedBand(memberId: string): CachedBand | null {
  try {
    const raw = sessionStorage.getItem(cacheKey(memberId));
    return raw ? (JSON.parse(raw) as CachedBand) : null;
  } catch { return null; }
}

export function useBusinessSnapshot(member: TeamMember | null, role: string | null | undefined, isSuperUser: boolean) {
  const [core, setCore] = useState<CoreSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const aliveRef = useRef(true);
  // Generation counter — aliveRef alone can't cancel a SUPERSEDED load (the
  // next effect run sets it back to true, reviving the stale chain's guards;
  // audit fix, Aug 24). Each run captures its own id and bails if outrun.
  const runSeqRef = useRef(0);
  const artifactsRef = useRef<Phase1Artifacts | null>(null);
  const [lenses, setLenses] = useState<LensData | null>(null);
  const [lensesLoading, setLensesLoading] = useState(false);

  const refresh = useCallback(() => { artifactsRef.current = null; setLenses(null); setTick((t) => t + 1); }, []);

  /** Phase 2 — the time↔money lenses. Self-scoped, loaded on first demand. */
  const loadLenses = useCallback(async () => {
    const art = artifactsRef.current;
    if (!art || lenses || lensesLoading) return;
    setLensesLoading(true);
    try {
      const me = art.me;
      const own = (d: SnapshotDeal) => d.owner_id === me || d.split_rm_id === me;
      const ownLive = art.live.filter(own);
      const ownQueue = art.queue.filter(own);
      const paidLines = myLines(art.queueGroups, me).filter((it) => it.deal.commission_paid);
      const projLines = myLines(art.projGroups, me);
      const signedDeals = [...ownLive, ...ownQueue].filter(
        (d) => d.install_agreement_signed === true || d.commission_ready_date,
      );

      const today = todayET();
      const d90 = new Date(); d90.setDate(d90.getDate() - 90);
      const from90 = d90.toISOString().slice(0, 10);
      const since30 = new Date(Date.now() - 30 * 86400000).toISOString();

      const [effort, terminal, claims, shiftsRes] = await Promise.all([
        fetchCityEffort(me, from90, today),
        fetchMyTerminalDeals([me]),
        fetchMyClaimOutcomes(me),
        supabase.rpc("get_canvas_audit_shifts", { p_start: since30, p_end: new Date().toISOString(), p_user: me }),
      ]);
      if (!aliveRef.current) return;

      // Money-per-city + sources use the paid line items (the finance math).
      const paidByDeal = new Map<string, number>();
      for (const it of paidLines) paidByDeal.set(it.deal.id, (paidByDeal.get(it.deal.id) ?? 0) + it.amount);
      const rosterByName = new Map<string, string>();
      for (const m of Object.values(art.members)) rosterByName.set((m.full_name ?? "").trim().toLowerCase(), m.role ?? "");

      const allMyDeals = new Map<string, SnapshotDeal>();
      for (const d of [...ownLive, ...ownQueue, ...terminal]) allMyDeals.set(d.id, d);

      const historyIds = [...allMyDeals.keys()];
      const history = await fetchStageHistory(historyIds);
      if (!aliveRef.current) return;

      const comms = await fetchCommCounts(me, [...ownLive, ...ownQueue].map((d) => d.id), since30);
      if (!aliveRef.current) return;

      const shiftSum = sumShifts(((shiftsRes.data ?? []) as AuditShiftRow[]));

      setLenses({
        geo: cityLens(effort, paidLines, projLines, signedDeals),
        sources: leadSourceLens([...allMyDeals.values()], paidByDeal, rosterByName),
        stages: stageFlow(history, ownLive, terminal),
        claims: { agg: claimsAgg(claims), byCarrier: claimsByCarrier(claims) },
        activity: activityMix(shiftSum, comms.calls, comms.texts),
      });
    } catch {
      // best-effort — the lens tab shows its own empty state
    } finally {
      if (aliveRef.current) setLensesLoading(false);
    }
  }, [lenses, lensesLoading]);

  useEffect(() => {
    aliveRef.current = true;
    if (!member) return;
    const me = member.id;
    const canSupp = canSeeSupplements(role ?? null, isSuperUser);
    const runId = ++runSeqRef.current;
    const stale = () => !aliveRef.current || runSeqRef.current !== runId;
    (async () => {
      try {
        // Roster first — scope ids for TL/ASM depend on it. Rates ride along:
        // team_roster.commission_rate is neutralized (Sep 9, 2026) and the
        // real rates come from visible_commission_rates() (self / team /
        // everyone for leadership). Fatal on error — earnings must not
        // silently compute at the 10% base.
        const [{ data: rosterData, error: rosterErr }, ratesRes] = await Promise.all([
          supabase.from("team_roster").select("*").eq("is_active", true),
          supabase.rpc("visible_commission_rates"),
        ]);
        if (rosterErr) throw new Error(rosterErr.message);
        if (ratesRes.error) throw new Error(ratesRes.error.message);
        const roster = (rosterData ?? []) as TeamMember[];
        const members: Record<string, TeamMember> = {};
        roster.forEach((m) => { members[m.id] = m; });
        mergeRates(members, ratesMapFromRows(ratesRes.data));
        const scope = moneyScopeIds(member, roster);

        const weekStart = mondayOf();
        const jan1 = `${todayET().slice(0, 4)}-01-01T00:00:00-05:00`; // ET New Year (always EST)

        const [dealsRes, wallRes, goalsRes, lbRes, spiffRes, drawRes, commitRes] =
          await Promise.allSettled([
            fetchSnapshotDeals(scope),
            // Builds = on the calendar this week (Oct 27, 2026) — the goal rule.
            fetchWallStats("this_week", { buildsOnCalendar: true }),
            fetchWeeklyGoalsMap(),
            fetchLeaderboard(),
            fetchMySpiffs(me, jan1),
            fetchDrawBalance(me),
            fetchMyCommitmentRow(me, weekStart),
          ]);
        if (stale()) return;
        if (dealsRes.status === "rejected") throw new Error(String(dealsRes.reason?.message ?? dealsRes.reason));

        const { queue, live } = dealsRes.value;
        const projDeals = projectionSet(live);
        const projById = new Map<string, SnapshotDeal>(projDeals.map((d) => [d.id, d]));

        // One canvasser map covers both groupByPerson runs (carve-out fees).
        // Sep 25, 2026: plus the stored payout ledger for the queue (RLS-scoped
        // to my own lines / my downline) — paid history renders what was
        // actually paid instead of a recompute. Fatal on error, like rates.
        const [canvasserByDeal, storedRows] = await Promise.all([
          fetchCanvasserByDeal([...new Set([...queue.map((d) => d.id), ...projDeals.map((d) => d.id)])]),
          fetchCommissionPayments({ dealIds: queue.map((d) => d.id) }),
        ]);
        if (stale()) return;

        // The two money runs every card derives from (single source, no drift).
        const queueGroups = groupByPersonStored(queue, members, canvasserByDeal, indexPayments(storedRows));
        const projGroups = groupByPerson(projDeals, members, canvasserByDeal);
        artifactsRef.current = { me, queue, live, queueGroups, projGroups, members };

        const earnings = earningsByMonth(queueGroups, me, 12);
        const crewYear = crewYearFor(todayET());
        const commissionYear = commissionYearSlice(queueGroups, me, crewYear.start, todayET());
        const outlook = commissionOutlook(queueGroups, projGroups, projById, me);
        const backlog = myBacklog(
          live.filter((d) => d.owner_id === me || d.split_rm_id === me),
          canSupp,
        );
        const myLive = live.filter((d) => d.owner_id === me || d.split_rm_id === me);
        const pipeline = pipelineSummary(myLive, canSupp);
        const cold = coldDeals(myLive);
        const held = heldCommissions(queueGroups, me);

        const wall = wallRes.status === "fulfilled" ? wallRes.value.stats[me] : undefined;
        const funnel = personalFunnel(wall);
        const goals = goalsRes.status === "fulfilled" ? goalsRes.value[me] : undefined;

        let crew: CrewSnippet = { rank: null, of: 0, pointsWeek: 0, perfectTotal: 0, spiffWonWeek: 0 };
        if (lbRes.status === "fulfilled") {
          const rows = lbRes.value.filter((r) => r.is_active);
          const sorted = [...rows].sort((a, b) => b.points_week - a.points_week);
          const idx = sorted.findIndex((r) => r.user_id === me);
          crew = {
            rank: idx >= 0 ? idx + 1 : null,
            of: sorted.length,
            pointsWeek: idx >= 0 ? sorted[idx].points_week : 0,
            perfectTotal: Math.round(((wall?.claims ?? 0) + (wall?.builds_scheduled ?? 0)) * 100) / 100,
            spiffWonWeek: 0,
          };
        }
        if (spiffRes.status === "fulfilled") {
          crew.spiffWonWeek = spiffRes.value
            .filter((s) => s.claimed_at && etDay(s.claimed_at) >= weekStart)
            .reduce((s, r) => s + r.amount, 0);
        }

        // TL/ASM team view — production per member + my override per member.
        const isLead = member.role === "team_lead" || member.role === "asm";
        const wallStats = wallRes.status === "fulfilled" ? wallRes.value.stats : {};
        const team = isLead
          ? teamSnapshot(queueGroups, projGroups, me, scope, members, wallStats)
          : null;
        const teamBacklog = isLead
          ? myBacklog(live, canSupp) // live is already scope-filtered (self + team)
          : null;

        const snapshot: CoreSnapshot = {
          earnings,
          outlook,
          backlog,
          pipeline,
          coldCount: cold.length,
          heldCount: held.length,
          cold,
          held,
          closedByMonth: closedDealsByMonth(queueGroups, me),
          spiffs: spiffRes.status === "fulfilled" ? spiffRes.value : [],
          funnel,
          goals,
          crew,
          drawBalance: drawRes.status === "fulfilled" ? drawRes.value.balance : 0,
          commitmentRow: commitRes.status === "fulfilled" ? commitRes.value : null,
          team,
          teamBacklog,
          commissionYear,
        };
        if (stale()) return;
        setCore(snapshot);
        setError(null);
        try {
          const nowMonth = todayET().slice(0, 7);
          const thisMonth = earnings.rows.find((r) => r.month === nowMonth);
          const cached: CachedBand = {
            earnedThisMonth: thisMonth?.paid ?? 0,
            monthSeries: earnings.rows.map((r) => r.paid),
            coming: outlook.pendingQueue + outlook.approvedAwaitingPay + outlook.projectedSignedUnbuilt,
            bookValue: pipeline.bookValue,
            liveCount: pipeline.liveCount,
          };
          sessionStorage.setItem(cacheKey(me), JSON.stringify(cached));
        } catch { /* cache is best-effort */ }
      } catch (e) {
        if (!stale()) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { aliveRef.current = false; };
  }, [member?.id, role, isSuperUser, tick]);

  return { core, error, refresh, lenses, lensesLoading, loadLenses };
}
